import React from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { 
  BookOpen, Users, Building2, Briefcase, TrendingUp, 
  Target, DollarSign, Calendar, ArrowRight, CheckCircle,
  AlertTriangle, Info, ChevronRight, Star
} from 'lucide-react';

const BusinessRules = () => {
  const { settings } = useSettings();
  const primaryColor = settings?.primaryColor || '#00b30c';

  const sections = [
    {
      id: 'flujo-principal',
      title: 'Flujo Principal del CRM',
      icon: ArrowRight,
      color: 'indigo',
      content: [
        {
          subtitle: 'Lead → Empresa → Negocio',
          description: 'El CRM sigue un flujo estructurado para gestionar las oportunidades de venta:',
          steps: [
            {
              step: '1. Registro de Lead',
              detail: 'Un lead es una persona que deja sus datos o se carga en la base de datos. Puede venir de diferentes fuentes: formulario web, referido, evento, etc.',
              important: 'El lead contiene información de contacto básica: nombre, email, teléfono, empresa (nombre).'
            },
            {
              step: '2. Creación de Empresa',
              detail: 'Cuando el lead avanza a la etapa "Contacto", se debe crear la empresa asociada con todos sus datos: NIT, dirección, sector, tamaño, etc.',
              important: 'La empresa agrupa todos los leads y negocios de una misma organización.'
            },
            {
              step: '3. Creación de Negocio',
              detail: 'Cuando el lead pasa a "Propuesta", se crea un negocio con los servicios cotizados, valor total y fecha estimada de cierre.',
              important: 'Un negocio representa una oportunidad de venta concreta con valor monetario.'
            },
            {
              step: '4. Cierre del Negocio',
              detail: 'El negocio se mueve a "Ganado" o "Perdido" cuando se define el resultado final de la negociación.',
              important: 'La fecha de cierre (actualCloseDate) se registra automáticamente al cambiar a estado ganado/perdido.'
            }
          ]
        }
      ]
    },
    {
      id: 'definiciones',
      title: 'Definiciones Clave',
      icon: BookOpen,
      color: 'blue',
      content: [
        {
          subtitle: 'Entidades Principales',
          items: [
            { term: 'Lead', definition: 'Persona que deja sus datos o se carga en la base de datos. Es el punto de entrada al embudo de ventas.' },
            { term: 'Empresa', definition: 'Organización a la que pertenece el lead. Agrupa múltiples leads y negocios. Tiene asignado un comercial responsable.' },
            { term: 'Negocio (Deal)', definition: 'Oportunidad de venta con valor monetario. Se crea cuando hay una propuesta concreta de servicios.' },
            { term: 'Comercial', definition: 'Usuario con rol "comercial" asignado a una empresa. Responsable de cerrar los negocios.' },
            { term: 'Embudo (Pipeline)', definition: 'Secuencia de etapas que sigue un lead desde el primer contacto hasta el cierre.' }
          ]
        }
      ]
    },
    {
      id: 'indicadores',
      title: 'Indicadores y Métricas',
      icon: TrendingUp,
      color: 'green',
      content: [
        {
          subtitle: 'Indicadores de Volumen',
          items: [
            { 
              term: 'Total Leads', 
              definition: 'Cantidad de leads creados en el período seleccionado. Se cuenta por fecha de creación (createdAt).',
              formula: 'COUNT(leads WHERE createdAt IN período)'
            },
            { 
              term: 'Negocios (Total)', 
              definition: 'Cantidad de negocios creados en el período. Incluye todos los estados.',
              formula: 'COUNT(deals WHERE createdAt IN período)'
            },
            { 
              term: 'Negocios Ganados', 
              definition: 'Negocios que se movieron a estado "ganado" en el período. Se cuenta por fecha de cierre real.',
              formula: 'COUNT(deals WHERE status="won" AND actualCloseDate IN período)'
            },
            { 
              term: 'Negocios Perdidos', 
              definition: 'Negocios que se movieron a estado "perdido" en el período.',
              formula: 'COUNT(deals WHERE status="lost" AND actualCloseDate IN período)'
            }
          ]
        },
        {
          subtitle: 'Indicadores de Valor',
          items: [
            { 
              term: 'Valor Ganado', 
              definition: 'Sumatoria de los valores de todos los negocios cerrados como ganados.',
              formula: 'SUM(deals.totalValue WHERE status="won" AND actualCloseDate IN período)'
            },
            { 
              term: 'Valor Promedio Deal', 
              definition: 'Ticket promedio por negocio ganado.',
              formula: 'Valor Ganado / Negocios Ganados'
            },
            { 
              term: 'Valor Pipeline Activo', 
              definition: 'Suma del valor de leads en etapas activas (no cerrados ni perdidos). Representa el potencial en negociación.',
              formula: 'SUM(leads.value WHERE stageId NOT IN [cerrado, perdido])'
            }
          ]
        },
        {
          subtitle: 'Indicadores de Conversión',
          items: [
            { 
              term: 'Tasa de Conversión Ganados', 
              definition: 'Porcentaje de negocios ganados sobre el total de negocios cerrados (ganados + perdidos).',
              formula: '(Negocios Ganados / (Ganados + Perdidos)) × 100'
            },
            { 
              term: 'Tasa de Pérdida', 
              definition: 'Porcentaje de negocios perdidos sobre el total cerrado. Indicador inverso: menor es mejor.',
              formula: '(Negocios Perdidos / (Ganados + Perdidos)) × 100'
            }
          ]
        },
        {
          subtitle: 'Indicadores de Tiempo',
          items: [
            { 
              term: 'Días para Cerrar', 
              definition: 'Promedio de días desde la creación del negocio hasta su cierre como ganado.',
              formula: 'AVG(actualCloseDate - createdAt) para deals ganados'
            }
          ]
        }
      ]
    },
    {
      id: 'etapas-embudo',
      title: 'Etapas del Embudo',
      icon: Target,
      color: 'purple',
      content: [
        {
          subtitle: 'Etapas Estándar',
          description: 'El embudo de ventas tiene las siguientes etapas por defecto:',
          stages: [
            { name: 'Nuevo', description: 'Lead recién ingresado, sin contacto inicial.', action: 'Realizar primer contacto en máximo 24 horas.' },
            { name: 'Contactado', description: 'Se estableció comunicación inicial con el lead.', action: 'Calificar interés y necesidades. Crear empresa si procede.' },
            { name: 'Calificado', description: 'Lead con interés confirmado y presupuesto potencial.', action: 'Agendar reunión de presentación.' },
            { name: 'Propuesta', description: 'Se envió cotización formal.', action: 'Crear negocio con servicios y valores. Seguimiento activo.' },
            { name: 'Negociación', description: 'Cliente evaluando propuesta, posible ajuste de términos.', action: 'Resolver objeciones. Negociar condiciones.' },
            { name: 'Ganado', description: 'Negocio cerrado exitosamente.', action: 'Iniciar proceso de implementación.' },
            { name: 'Perdido', description: 'Oportunidad no concretada.', action: 'Registrar motivo de pérdida para análisis.' }
          ]
        }
      ]
    },
    {
      id: 'comerciales',
      title: 'Gestión de Comerciales',
      icon: Users,
      color: 'orange',
      content: [
        {
          subtitle: 'Asignación de Comerciales',
          description: 'Los comerciales se asignan a nivel de empresa:',
          rules: [
            'Cada empresa tiene un comercial principal (responsable)',
            'Se pueden asignar comerciales adicionales para colaboración',
            'El comercial principal recibe alertas de nuevos leads de su empresa',
            'Las nuevas empresas pueden tener un comercial asignado automáticamente'
          ]
        }
      ]
    },
    {
      id: 'alertas',
      title: 'Sistema de Alertas',
      icon: AlertTriangle,
      color: 'red',
      content: [
        {
          subtitle: 'Tipos de Alertas',
          items: [
            { term: 'Alta Prioridad', definition: 'Leads sin seguimiento por más de 3 días o con fecha de cierre próxima.' },
            { term: 'Media Prioridad', definition: 'Tareas pendientes de revisión o leads en etapa estancada.' },
            { term: 'Baja Prioridad', definition: 'Recordatorios de seguimiento rutinario.' }
          ]
        }
      ]
    },
    {
      id: 'mejores-practicas',
      title: 'Mejores Prácticas',
      icon: Star,
      color: 'yellow',
      content: [
        {
          subtitle: 'Recomendaciones',
          rules: [
            'Responder nuevos leads en las primeras 24 horas',
            'Completar datos de empresa antes de enviar propuesta',
            'Crear el negocio con todos los servicios antes de mover a "Propuesta"',
            'Actualizar el estado del lead/negocio en tiempo real',
            'Registrar notas de cada interacción con el cliente',
            'Cerrar negocios perdidos con motivo de pérdida para análisis',
            'Revisar alertas diariamente',
            'Mantener actualizados los valores estimados de cada negocio'
          ]
        }
      ]
    }
  ];

  const colorClasses = {
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-600', light: 'bg-indigo-100' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', light: 'bg-blue-100' },
    green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', light: 'bg-green-100' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600', light: 'bg-purple-100' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600', light: 'bg-orange-100' },
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', light: 'bg-red-100' },
    yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-600', light: 'bg-yellow-100' }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reglas de Negocio</h1>
          <p className="text-slate-500 mt-1">Documentación completa del funcionamiento del CRM</p>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5" style={{ color: primaryColor }} />
          Contenido
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {sections.map(section => {
            const colors = colorClasses[section.color];
            return (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={`p-3 rounded-lg ${colors.bg} ${colors.border} border hover:opacity-80 transition-opacity flex items-center gap-2`}
              >
                <section.icon className={`w-4 h-4 ${colors.text}`} />
                <span className="text-sm font-medium text-slate-700">{section.title}</span>
              </a>
            );
          })}
        </div>
      </div>

      {/* Sections */}
      {sections.map(section => {
        const colors = colorClasses[section.color];
        const IconComponent = section.icon;

        return (
          <div 
            key={section.id} 
            id={section.id}
            className={`bg-white rounded-xl border ${colors.border} overflow-hidden`}
          >
            <div className={`${colors.bg} px-6 py-4 border-b ${colors.border}`}>
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${colors.light}`}>
                  <IconComponent className={`w-5 h-5 ${colors.text}`} />
                </div>
                {section.title}
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {section.content.map((block, blockIdx) => (
                <div key={blockIdx}>
                  {block.subtitle && (
                    <h3 className="text-lg font-semibold text-slate-800 mb-3">{block.subtitle}</h3>
                  )}
                  
                  {block.description && (
                    <p className="text-slate-600 mb-4">{block.description}</p>
                  )}

                  {/* Steps (for flow) */}
                  {block.steps && (
                    <div className="space-y-4">
                      {block.steps.map((step, stepIdx) => (
                        <div key={stepIdx} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${colors.light} mt-0.5`}>
                              <ChevronRight className={`w-4 h-4 ${colors.text}`} />
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-900">{step.step}</h4>
                              <p className="text-slate-600 mt-1">{step.detail}</p>
                              {step.important && (
                                <div className="mt-2 flex items-start gap-2 text-sm">
                                  <Info className={`w-4 h-4 ${colors.text} mt-0.5 flex-shrink-0`} />
                                  <span className="text-slate-700">{step.important}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Items (for definitions) */}
                  {block.items && (
                    <div className="space-y-3">
                      {block.items.map((item, itemIdx) => (
                        <div key={itemIdx} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                          <div className="flex items-start gap-3">
                            <div className={`p-1.5 rounded ${colors.light} mt-0.5`}>
                              <CheckCircle className={`w-4 h-4 ${colors.text}`} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-slate-900">{item.term}</h4>
                              <p className="text-slate-600 mt-1">{item.definition}</p>
                              {item.formula && (
                                <div className="mt-2 bg-white rounded border border-slate-200 px-3 py-1.5 inline-block">
                                  <code className="text-sm text-slate-700">{item.formula}</code>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Stages (for funnel) */}
                  {block.stages && (
                    <div className="space-y-3">
                      {block.stages.map((stage, stageIdx) => (
                        <div key={stageIdx} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full ${colors.light} flex items-center justify-center ${colors.text} font-bold text-sm`}>
                              {stageIdx + 1}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-slate-900">{stage.name}</h4>
                              <p className="text-slate-600 mt-1">{stage.description}</p>
                              <div className="mt-2 flex items-start gap-2 text-sm bg-white rounded border border-slate-200 p-2">
                                <ArrowRight className={`w-4 h-4 ${colors.text} mt-0.5 flex-shrink-0`} />
                                <span className="text-slate-700"><strong>Acción:</strong> {stage.action}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Rules (for best practices) */}
                  {block.rules && (
                    <ul className="space-y-2">
                      {block.rules.map((rule, ruleIdx) => (
                        <li key={ruleIdx} className="flex items-start gap-2">
                          <CheckCircle className={`w-5 h-5 ${colors.text} mt-0.5 flex-shrink-0`} />
                          <span className="text-slate-700">{rule}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Footer */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 text-center">
        <p className="text-slate-500 text-sm">
          Última actualización: {new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>
    </div>
  );
};

export default BusinessRules;
